import { Col, Form, Input, InputNumber, List, Row } from 'antd';
import { RevxButton } from 'components/RevxButton';
import { MarkupRange } from 'domains/markupDomain';
import { cloneDeep, uniqueId } from 'lodash-es';
import { clone } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RULE_PRECISION, RULE_TEXT_FIXED_LEN } from 'utils/validators';

const RangeForm = (props: {
  markupRange: MarkupRange;
  onApply: (range: MarkupRange) => void;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  useEffect(() => {
    form.setFieldsValue(props.markupRange);
  }, [form, props.markupRange]);
  const onSave = (values: Partial<MarkupRange>) => {
    const updated: MarkupRange = { ...props.markupRange };
    updated.lowCost = Number(values.lowCost);
    if (updated.hasHighCost) updated.highCost = Number(values.highCost);
    if (updated.highCost <= updated.lowCost) {
      form.setFields([{ name: 'highCost', errors: [t('common.highLow')] }]);
      return;
    }
    updated.markupPct = Number(values.markupPct);
    props.onApply(updated);
  };
  return (
    <Form
      size="small"
      layout="inline"
      form={form}
      initialValues={props.markupRange}
      onFinish={onSave}
      style={{ paddingTop: 8 }}
    >
      <Form.Item name="lowCost" rules={[RULE_PRECISION(2)]} label="Low">
        <Input prefix="$" id={uniqueId('hightCost')} style={{ width: 80 }} />
      </Form.Item>
      {props.markupRange.hasHighCost ? (
        <Form.Item name="highCost" rules={[RULE_PRECISION(2)]} label="High">
          <Input prefix="$" id={uniqueId('hightCost')} style={{ width: 80 }} />
        </Form.Item>
      ) : (
        <Form.Item label="High" rules={[RULE_TEXT_FIXED_LEN(10)]}>
          <Input prefix="$" disabled value="MAX" style={{ width: 80 }} />
        </Form.Item>
      )}
      <Form.Item name={'markupPct'} label="Markup">
        <InputNumber<number>
          id={uniqueId('markupPct')}
          min={-100}
          max={100}
          formatter={(v) => `${v}%`}
          parser={(v) => Number(v?.replace('%', ''))}
        ></InputNumber>
      </Form.Item>
      <Form.Item>
        <RevxButton type="link" key="apply" htmlType="submit">
          {t('common.apply')}
        </RevxButton>
      </Form.Item>
    </Form>
  );
};

const RangeList = (props: {
  markupRanges: MarkupRange[];
  onSave: (ranges: MarkupRange[]) => void;
}) => {
  const { t } = useTranslation();

  const [ranges, setRanges] = useState<MarkupRange[]>([]);

  useEffect(() => {
    const ranges = cloneDeep(props.markupRanges).sort(
      (a, b) => a.lowCost - b.lowCost
    );
    const highest = ranges.find((r) => !r.hasHighCost);
    if (highest) highest.highCost = Number.POSITIVE_INFINITY;
    setRanges(ranges);
  }, [props.markupRanges]);

  const onRangeApply = (toApplyRange: MarkupRange) => {
    if (ranges.length === 0) {
      // this should never happen
      return;
    } else {
      let updatedRanges: MarkupRange[] = [];
      for (let i = 0; i < ranges.length; i++) {
        if (
          ranges[i].highCost <= toApplyRange.lowCost ||
          ranges[i].lowCost >= toApplyRange.highCost
        ) {
          // no overlapping
          updatedRanges.push(clone(ranges[i]));
          continue;
        } else if (ranges[i].lowCost < toApplyRange.lowCost) {
          // right partial overlapping
          const r = clone(ranges[i]);
          r.highCost = toApplyRange.lowCost;
          r.hasHighCost = true;
          updatedRanges.push(r);
          continue;
        } else if (ranges[i].highCost <= toApplyRange.highCost) {
          // full overlapping
          continue;
        } else {
          // left partial overlapping
          ranges[i].lowCost = toApplyRange.highCost;
          updatedRanges.push(clone(ranges[i]));
          continue;
        }
      }
      updatedRanges.push(toApplyRange);
      updatedRanges.sort((a, b) => a.lowCost - b.lowCost);
      setRanges(updatedRanges);
    }
  };

  return (
    <List
      dataSource={ranges}
      renderItem={(item, index) => {
        return (
          <RangeForm key={index} markupRange={item} onApply={onRangeApply} />
        );
      }}
      footer={
        <Row>
          <Col>
            <RevxButton
              key="save"
              onClick={() => props.onSave(ranges)}
              type="primary"
            >
              {t('common.save')}
            </RevxButton>
          </Col>
        </Row>
      }
    />
  );
};

export default RangeList;
